/**
 * Shop price-display helpers for the IP-estimated VAT feature (Task 2).
 *
 * The shop shows a GROSS (VAT-inclusive) headline whenever VAT is live and the visitor's country has
 * a real, positive rate — driven by the RATE, not by EU membership, so a non-EU country we charge VAT
 * in (e.g. Switzerland, MWST 8.1%) shows gross too. Everything else — a failed estimate (the API
 * never throws; see `vatApi.getVatEstimate`'s fallback), a still-loading estimate (`null`/`undefined`),
 * or a 0% rate (a country we don't charge VAT in) — falls back to the plain NET price. That single
 * `shouldShowGross` check is what keeps the price block from ever blocking or crashing on a bad/slow/
 * missing estimate. (`is_eu` governs reverse-charge eligibility at checkout, NOT whether VAT is shown.)
 *
 * All money math goes through `vatMoney`'s integer-cents helpers (`toCents`/`grossCents`/
 * `fromCents`) — never `price * 1.19` float arithmetic.
 *
 * This module stays a pure util (no react-i18next import, no hooks) so it's usable outside a
 * component tree and trivially unit-testable — `vatPriceNote` returns an i18n KEY, not rendered
 * text; the consuming component (`VatPriceBlock`) calls `t()` on it so the note is translated in
 * all 6 locales instead of the hardcoded English string this used to be.
 */

import {toCents, fromCents, grossCents} from './vatMoney'

/**
 * The slice of `VatEstimate` these helpers need. Kept narrow (rather than importing the full
 * `VatEstimate` type) so callers can pass a plain `{is_eu, rate}` literal in tests.
 */
export interface VatEstimateLike {
  is_eu: boolean
  rate: number
  /**
   * The resolved country (ISO code), or null/absent when the shop couldn't resolve one yet (IP
   * header missing, estimate still loading/failed). Used only to tell a RESOLVED zero-VAT country
   * (e.g. UAE, US → no VAT, show nothing) apart from an UNRESOLVED one (rate not known yet → the
   * "VAT calculated at checkout" note is still meaningful).
   */
  country_code?: string | null
  /**
   * The single on/off switch, mirrored from the backend (VAT_ENABLED flag AND the go-live date).
   * When this is not exactly true the whole shop VAT UI is suppressed — no gross price, no note, no
   * business/UST-ID fields, no MwSt line — so the shop looks exactly as it did pre-VAT. Absent/
   * false is the safe default (a failed or pre-go-live estimate hides VAT).
   */
  vat_active?: boolean
}

/** i18n keys for the one-line note shown under a shop price — see `vatPriceNote` below. */
export const VAT_INCL_NOTE_KEY = 'VAT_INCL_NOTE'
export const VAT_AT_CHECKOUT_NOTE_KEY = 'VAT_AT_CHECKOUT_NOTE'

/** The master gate: is VAT live (flag + go-live date)? Everything VAT-visual keys off this. */
export function isVatActive(estimate: VatEstimateLike | null | undefined): boolean {
  return estimate?.vat_active === true
}

/** True once the shop has resolved a billing country for this estimate (ISO code present). */
function hasResolvedCountry(estimate: VatEstimateLike | null | undefined): boolean {
  return !!estimate?.country_code
}

/**
 * Whether to show the one-line VAT note under a shop price. VAT must be live, AND either:
 *  - gross is shown (rate > 0) → "incl. VAT" note, or
 *  - the country isn't resolved yet (rate unknown) → "VAT calculated at checkout" note.
 * A RESOLVED zero-VAT country (e.g. UAE, US — rate 0) charges no VAT, so NO note is shown
 * (showing "VAT at checkout" there is misleading — there will be none).
 */
export function showVatNote(estimate: VatEstimateLike | null | undefined): boolean {
  if (!isVatActive(estimate)) return false
  return shouldShowGross(estimate) || !hasResolvedCountry(estimate)
}

/**
 * Whether a price should be shown gross (VAT-inclusive) for this estimate: VAT must be live AND the
 * country has a real, positive rate. Gated on the RATE, not `is_eu`, so a non-EU country we charge VAT
 * in (Switzerland, 8.1%) shows gross. `null`/`undefined` (loading or never fetched), a 0 rate, or VAT
 * not active all read as "show net".
 */
export function shouldShowGross(estimate: VatEstimateLike | null | undefined): boolean {
  return isVatActive(estimate) && !!estimate?.rate
}

/**
 * The i18n key for the one-line note shown under a shop price, matching the gross/net decision
 * above. Returns a KEY, not rendered text — the caller (`VatPriceBlock`) runs it through `t()`.
 */
export function vatPriceNote(estimate: VatEstimateLike | null | undefined): string {
  return shouldShowGross(estimate) ? VAT_INCL_NOTE_KEY : VAT_AT_CHECKOUT_NOTE_KEY
}

/** Net price, as integer cents, grossed up when `shouldShowGross` — the shared cents-safe core. */
function displayCents(
  netPrice: string | number,
  estimate: VatEstimateLike | null | undefined
): number {
  const netCents = toCents(netPrice)
  if (!shouldShowGross(estimate)) return netCents
  return grossCents(netCents, estimate!.rate * 100)
}

/**
 * The number to render as a shop price: gross for a personal EU visitor, net otherwise. Exact
 * integer-cents math throughout (see module doc) — no float residue.
 */
export function vatDisplayAmount(
  netPrice: string | number,
  estimate: VatEstimateLike | null | undefined
): number {
  return fromCents(displayCents(netPrice, estimate))
}

/**
 * The "you save" amount between an original and a discounted net price, in the same display mode
 * (gross/net) as `vatDisplayAmount` — both sides are grossed up (or left net) before differencing,
 * so VAT never changes what the member actually saves.
 */
export function vatSaveAmount(
  originalNetPrice: string | number,
  discountedNetPrice: string | number,
  estimate: VatEstimateLike | null | undefined
): number {
  return fromCents(
    displayCents(originalNetPrice, estimate) - displayCents(discountedNetPrice, estimate)
  )
}
